/* eslint-disable react-hooks/exhaustive-deps */

import {
    ReactNode,
    createContext,
    FC,
    useReducer,
    useEffect,
    Dispatch
} from 'react';

import isEqual from 'lodash/isEqual';

import {
    DfpTargeting,
    AdvertContextType,
    ZPG_DFP_NETWORK,
    DFP_AD_UNIT,
    AdvertContextAction,
    ACTION_TYPE,
    Slot,
    AD_UNIT_SECTION,
    AD_UNIT_SECTION_NEW,
    AD_UNIT_PAGE_TYPE
} from './types';
import usePrevious from './usePreviousHook';
import DfpManager from './dfpManager';

type defaultContext = {
    state: AdvertContextType;
    dispatch: Dispatch<AdvertContextAction>;
    mountSlot: (slot: Slot) => void;
    unmountSlot: (slot: Pick<Slot, 'id'>) => void;
};

const defaultState: AdvertContextType = {
    dfpInitialised: false,
    adBridgInitialised: false,
    slots: {},
    targeting: {},
    dfpNetworkId: ZPG_DFP_NETWORK.ID,
    adUnitCode: DFP_AD_UNIT.DEFAULT,
    showPlaceHolderContent: false,
    debugMode: false
};

const defaultValue: defaultContext = {
    state: { ...defaultState },
    dispatch: () => ({}),
    mountSlot: () => void 0,
    unmountSlot: () => void 0
};

export const advertReducer = (
    state: AdvertContextType,
    action: AdvertContextAction
): AdvertContextType => {
    const slotsCopy = { ...state.slots };
    switch (action.type) {
        case ACTION_TYPE.SET_DFP_MANAGER: {
            action.payload.refresh();
            action.payload.defineSlots(slotsCopy);
            const keys = Object.keys(slotsCopy);
            keys.forEach((key) => (slotsCopy[key].isDefined = true));
            return {
                ...state,
                dfpManager: action.payload,
                slots: slotsCopy
            };
        }
        case ACTION_TYPE.MOUNT_SLOT: {
            const slotToMount = { ...action.payload };
            return {
                ...state,
                slots: { ...state.slots, [slotToMount.id]: slotToMount }
            };
        }
        case ACTION_TYPE.UNMOUNT_SLOT:
            delete slotsCopy[action.payload.id];
            return {
                ...state,
                slots: slotsCopy
            };
        case ACTION_TYPE.SET_TARGETING:
            return {
                ...state,
                targeting: { ...action.payload }
            };
        case ACTION_TYPE.SET_INITIALISED:
            return {
                ...state,
                dfpInitialised: true
            };
        case ACTION_TYPE.SET_SHUTDOWN:
            return {
                ...state,
                adBridgInitialised: false
            };
        case ACTION_TYPE.SET_ADBRIDG_INITIALISED:
            return {
                ...state,
                adBridgInitialised: true
            };
        default:
            return state;
    }
};

export const AdvertContext = createContext(defaultValue);
AdvertContext.displayName = 'AdvertContext';

export type AdvertProviderProps = {
    children?: ReactNode;
    value: {
        adUnitCode?: DFP_AD_UNIT.DEFAULT;
        dfpNetworkId?: ZPG_DFP_NETWORK.ID;
        targeting: DfpTargeting;
        showPlaceHolderContent?: boolean;
        initialState?: AdvertContextType;
        debugMode?: boolean;
        pageNumber?: number;
        adBridgPage?: AD_UNIT_SECTION;
        adBridgPageNew?: AD_UNIT_SECTION_NEW;
        pageType: AD_UNIT_PAGE_TYPE;
        delayInit?: boolean;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatchOverride?: any;
};

const AdvertProvider: FC<React.PropsWithChildren<AdvertProviderProps>> = ({
    children,
    value: {
        targeting,
        dfpNetworkId = ZPG_DFP_NETWORK.ID,
        adUnitCode = DFP_AD_UNIT.DEFAULT,
        showPlaceHolderContent = false,
        initialState = defaultState,
        debugMode = false,
        pageNumber,
        adBridgPage,
        adBridgPageNew,
        pageType,
        delayInit = false
    },
    dispatchOverride
}) => {
    const [state, dispatch] = useReducer(advertReducer, {
        ...initialState,
        dfpNetworkId,
        adUnitCode,
        targeting,
        showPlaceHolderContent,
        debugMode
    });

    const { dfpInitialised, dfpManager, adBridgInitialised } = state;

    const prevTargeting = usePrevious(targeting);
    const prevPageNumber = usePrevious(pageNumber);

    const mountSlot = (slot: Slot) => {
        if (dfpManager) {
            dfpManager.defineSlot(slot);
            slot.isDefined = true;
        }
        dispatch({ type: ACTION_TYPE.MOUNT_SLOT, payload: slot });
    };

    const unmountSlot = (slot: Pick<Slot, 'id'>) => {
        dfpManager?.destroySlot(slot.id);
        dispatch({ type: ACTION_TYPE.UNMOUNT_SLOT, payload: slot });
    };

    // load DfpManager once on client side mount
    const isAdBridgEnabled = true;
    useEffect(() => {
        const dfpManagerInstance = new DfpManager(
            targeting,
            debugMode,
            isAdBridgEnabled,
            adBridgPage,
            adBridgPageNew,
            pageType
        );
        dispatch({
            type: ACTION_TYPE.SET_DFP_MANAGER,
            payload: dfpManagerInstance
        });
        dfpManagerInstance.log('mounted');
    }, [isAdBridgEnabled]);

    useEffect(() => {
        if (dfpManager && !dfpInitialised && !delayInit) {
            dfpManager.init(() => {
                dispatch({ type: ACTION_TYPE.SET_INITIALISED });
            });
        }
        if (!adBridgInitialised && !delayInit) {
            dfpManager?.initAdbridg();
            dispatch({ type: ACTION_TYPE.SET_ADBRIDG_INITIALISED });
        }
    }, [dfpManager, dfpInitialised, adBridgInitialised, delayInit]);

    const updatedPageNumber =
        typeof pageNumber !== 'undefined' &&
        prevPageNumber &&
        !isEqual(prevPageNumber, pageNumber);
    const updatedTargeting =
        typeof targeting !== 'undefined' &&
        prevTargeting &&
        !isEqual(prevTargeting, targeting);

    useEffect(() => {
        if (dfpManager && updatedTargeting) {
            dispatch({ type: ACTION_TYPE.SET_TARGETING, payload: targeting });
            dfpManager.log('updateTargeting');
            dfpManager.configureTargeting(targeting);
            dfpManager.refreshTargeting();
        }
        if (
            dfpManager &&
            dfpInitialised &&
            (updatedPageNumber || updatedTargeting)
        ) {
            dfpManager.log('refreshcheck');
            dfpManager.shutdown();
            dispatch({ type: ACTION_TYPE.SET_SHUTDOWN });
            dfpManager.refresh();
            dfpManager.initAdbridg();
            dispatch({ type: ACTION_TYPE.SET_ADBRIDG_INITIALISED });
        }
    }, [
        updatedTargeting,
        updatedPageNumber,
        prevTargeting,
        dfpManager,
        dfpInitialised
    ]);

    return (
        <AdvertContext.Provider
            value={{
                state,
                dispatch: dispatchOverride || dispatch,
                mountSlot,
                unmountSlot
            }}
        >
            {children}
        </AdvertContext.Provider>
    );
};

export const AdvertProviderWithOptimizely = AdvertProvider;
